<template>
  <div data-test-id="html-editor">
    <div class="row">
      <div
        class="col"
        :class="blockOrSection.class"
        :style="blockOrSection.style"
        @keydown.stop
        @click.prevent
      >
        <div
          id="gjs"
          :class="{ 'editor-highlighted': highlight }"
        />
      </div>
    </div>

    <!-- Missing Alt Text Warning Banner -->
    <q-banner
      v-if="!$_.isEmpty(imagesWithoutAltText)"
      dense
      rounded
      class="bg-orange text-white q-mb-sm"
    >
      <template #avatar>
        <q-icon name="warning" />
      </template>
      <div class="text-body2">
        {{ $tc('common.image', $_.size(imagesWithoutAltText)) }} missing alt text.
        Click on an image and select the settings icon to add alt text for better accessibility and SEO.
      </div>
    </q-banner>
  </div>
</template>

<script>

import 'grapesjs/dist/css/grapes.min.css'

import grapesjs from 'grapesjs'
import { imgixHost } from 'src/config'
import CustomBlock from 'src/models/page/CustomBlock'
import ImageAsset from 'src/models/page/ImageAsset'
import Section from 'src/models/page/Section'
import blocks from 'src/modules/admin/html-editor/plugins/blocks'
import codeEditorPlugin from 'src/modules/admin/html-editor/plugins/code-editor'
import dynamicBlocks from 'src/modules/admin/html-editor/plugins/dynamic-blocks'
import gridComponents from 'src/modules/admin/html-editor/plugins/grid-components'

async function handleUpload (e) {
  const files = e.dataTransfer ? e.dataTransfer.files : e.target.files

  const mimeTypeMatcher = /^(.+)\/(.+)$/

  const results = []
  for (const file of files) {
    const match = mimeTypeMatcher.exec(file.type)
    let type
    if (match) {
      type = match[1] // The first part in the MIME, "image" in image/png
    } else {
      type = file.type
    }

    const { key } = await htmlEditor.$directUpload.uploadFile(file, true)
    results.push({
      src: imgixHost + '/' + key + '?max-w=1000&fit=fill&auto=format,compress',
      name: file.name,
      type
    })
  }

  this.onUploadResponse({ data: results })
}

let htmlEditor

export default {
  name: 'HtmlEditor',
  props: {
    blockOrSection: {
      type: [CustomBlock, Section],
      required: true
    },
    customBlocks: {
      type: CustomBlock,
      default: {
        data: []
      }
    },
    baseStyles: {
      type: String,
      required: false,
      default: null
    },
    baseClass: {
      type: String,
      required: false,
      default: null
    },
    highlight: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      editor: null,
      initialHtmlContent: null,
      initialCssContent: null,
      settingContentFromEditor: false,
      altCheckTick: 0
    }
  },
  computed: {
    imagesWithoutAltText () {
      // tick for reactivity
      /* eslint-disable-next-line no-unused-vars */
      const _tick = this.altCheckTick
      if (!this.editor) return []

      try {
        const html = this.editor.getHtml()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const images = doc.querySelectorAll('img:not([alt]), img[alt=""]')

        return Array.from(images)
      } catch (error) {
        return []
      }
    }
  },
  watch: {
    'blockOrSection.content' () {
      if (this.settingContentFromEditor) return

      this.updateEditorFromContent()
    },
    'blockOrSection.styles' () {
      if (this.settingContentFromEditor) return

      this.updateEditorFromStyles()
    }
  },
  async mounted () {
    htmlEditor = this

    this.initialHtmlContent = this.blockOrSection.content.toString()
    await this.$nextTick()

    const editor = grapesjs.init({
      avoidInlineStyle: false,
      height: '600px',
      container: '#gjs',
      showOffsets: true,
      fromElement: true,
      forceClass: false,
      canvas: {
        styles: ['custom-page.css']
      },
      parser: {
        optionsHtml: {
          allowScripts: true
        }
      },

      assetManager: {
        uploadFile: handleUpload
      },
      plugins: [gridComponents, blocks, codeEditorPlugin, dynamicBlocks(this.customBlocks.data)],
      storageManager: { type: 'onChange' }
    })

    this.editor = editor
    const cmdm = editor.Commands
    const pnm = editor.Panels

    cmdm.add('canvas-clear', function () {
      if (confirm('Are you sure to clean the canvas?')) {
        editor.DomComponents.clear()
        setTimeout(function () { localStorage.clear() }, 0)
      }
    })
    cmdm.add('set-device-desktop', {
      run: function (ed) { ed.setDevice('Desktop') },
      stop: function () {}
    })
    cmdm.add('set-device-tablet', {
      run: function (ed) { ed.setDevice('Tablet') },
      stop: function () {}
    })
    cmdm.add('set-device-mobile', {
      run: function (ed) { ed.setDevice('Mobile portrait') },
      stop: function () {}
    })

    // Show borders by default
    pnm.getButton('options', 'sw-visibility').set('active', 1)

    // Store and load events
    editor.on('storage:start', () => {
      this.updateContentFromEditor()
    })

    // Do stuff on load
    editor.on('load', function () {
      // Load and show settings and style manager
      const openTmBtn = pnm.getButton('views', 'open-tm')
      openTmBtn && openTmBtn.set('active', 1)
      const openSm = pnm.getButton('views', 'open-sm')
      openSm && openSm.set('active', 1)

      // Open block manager
      const openBlocksBtn = editor.Panels.getButton('views', 'open-blocks')
      openBlocksBtn && openBlocksBtn.set('active', 1)
    })

    // Alt text checks
    editor.on('component:add', cmp => { if (cmp?.is?.('image')) this.bumpAltCheckTick() })
    editor.on('component:remove', cmp => { if (cmp?.is?.('image')) this.bumpAltCheckTick() })
    editor.on('component:update:attributes', cmp => {
      if (cmp?.is?.('image')) this.bumpAltCheckTick()
    })
    editor.on('component:update:src', cmp => {
      if (cmp?.is?.('image')) this.bumpAltCheckTick()
    })
    editor.on('paste', () => this.bumpAltCheckTick())
    editor.on('storage:start', () => this.bumpAltCheckTick())
    editor.on('load', () => this.bumpAltCheckTick())

    this.updateEditorFromContent()
    this.updateEditorFromStyles()
    this.addImageAssetsToEditor()
    this.addBaseStyles()
    this.addBaseClass()
  },
  methods: {
    updateEditorFromContent () {
      this.editor.setComponents(this.blockOrSection.content.toString())
    },
    updateEditorFromStyles () {
      if (this.blockOrSection.styles?.length) {
        this.editor.setStyle(this.blockOrSection.styles.toString())
      }
    },
    async updateContentFromEditor () {
      this.settingContentFromEditor = true
      this.blockOrSection.content = this.editor.getHtml()
      this.blockOrSection.styles = this.editor.getCss({ avoidProtected: true })
      await this.$nextTick()
      this.settingContentFromEditor = false
    },
    async addImageAssetsToEditor () {
      const assetManager = this.editor.AssetManager

      const imageAssets = new ImageAsset({}, {
        isCollection: true,
        queryName: 'imageAssets',
        variables: {
          type: 'Site'
        },
        fields: 'default'
      })

      await imageAssets.fetch(true)
      assetManager.add(this.formatImageAssetsForEditor(imageAssets.data, 'Site'))

      // imageAssets.options.variables.type = 'Circuit'
      // await imageAssets.fetch(true)
      // assetManager.add(this.formatImageAssetsForEditor(imageAssets.data, 'Circuit'))

      // imageAssets.options.variables.type = 'User'
      // await imageAssets.fetch(true)
      // assetManager.add(this.formatImageAssetsForEditor(imageAssets.data, 'User'))
    },
    formatImageAssetsForEditor (imageAssets, category) {
      return _.map(imageAssets, imageAsset => {
        return {
          category,
          src: imageAsset.imageUrl,
          type: 'image'
        }
      })
    },
    addBaseStyles () {
      if (!this.baseStyles) return

      const iFrame = this.editor.Canvas.getFrameEl()
      const style = iFrame.contentDocument.createElement('style')

      style.textContent = this.baseStyles

      iFrame.contentDocument.head.appendChild(style)
    },
    addBaseClass () {
      if (!this.baseClass) return

      this.editor.Canvas.getFrameEl().contentDocument.body.classList.add(this.baseClass)
    },
    bumpAltCheckTick () {
      this.altCheckTick++
    }
  }
}

</script>

<style lang="scss" scoped>
.editor-highlighted {
  outline: 4px solid $amber-6;
}
</style>
